// Common
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// RX
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

// Type
import { Account } from '@frontendlib/common/types/account';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { Stitch } from '@modules/common/types/stitch';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { Message } from '@modules/messages/types/message';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { Project } from '@modules/tasks/types/project';
import { Task } from '@modules/tasks/types/task';
import { StitchType } from '@sharedlib/common/types/StitchType';

// Service
import { Calendar } from '@modules/calendar-app/types/calendar';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';

/** The route's own spelling of the type parameter (see FULL_FORM_POPUP_PATH). */
const STITCH_TYPE_PARAM = 'stitch-type';

@Component({
  selector: 'app-full-form-popup',
  templateUrl: './full-form-popup.component.html',
  styleUrls: ['./full-form-popup.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FullFormPopupComponent implements OnInit, OnDestroy {
  // Public
  public stitchType: StitchType;
  public stitchItem: Stitch;
  public account: Account;

  // Private
  private alive = new Subject<void>();

  // Type-safe getters for each specific type
  get asMessageFolder(): MessageFolder {
    return this.stitchItem as MessageFolder;
  }

  get asMessage(): Message {
    return this.stitchItem as Message;
  }

  get asCalendarEvent(): CalendarEvent {
    return this.stitchItem as CalendarEvent;
  }

  get asCalendar(): Calendar {
    return this.stitchItem as Calendar;
  }

  get asProject(): Project {
    return this.stitchItem as Project;
  }

  get asTask(): Task {
    return this.stitchItem as Task;
  }

  get asNotebook(): Notebook {
    return this.stitchItem as Notebook;
  }

  get asNote(): Note {
    return this.stitchItem as Note;
  }

  get asGroup(): Group {
    return this.stitchItem as Group;
  }

  get asContact(): Contact {
    return this.stitchItem as Contact;
  }

  get asFolder(): Folder {
    return this.stitchItem as Folder;
  }

  get asFile(): File {
    return this.stitchItem as File;
  }

  get asSheetFolder(): SheetFolder {
    return this.stitchItem as SheetFolder;
  }

  get asSheet(): Sheet {
    return this.stitchItem as Sheet;
  }

  constructor(
    private route: ActivatedRoute,
    private stitchServiceFactory: StitchServiceFactory,
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(
        tap((params) => {
          this.stitchType = params[STITCH_TYPE_PARAM];
          document.title = params[STITCH_TYPE_PARAM];
        }),
        switchMap((params) =>
          this.stitchServiceFactory.getServiceByStitchType(params[STITCH_TYPE_PARAM]).getItem(params['id']),
        ),
        takeUntil(this.alive),
      )
      .subscribe((item: Stitch) => {
        this.stitchItem = item;
      });
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  close() {
    window.close();
  }
}
